import { useEffect, useState } from 'react'

import { useClipLoaderTimeout } from './useClipLoaderTimeout'

import { IncidentType } from '@/graphql/generated/schemas'

import { incidentTypesWithoutVideo } from '../utils/helpers'

interface UseClipLoaderStateIProps {
  incidentId: string
  incidentType: IncidentType
  hasVideoClip: boolean
  createdAt: string
  isLoading: boolean
}

interface UseClipLoaderStateResultI {
  shouldShowClipLoader: boolean
  shouldShowIncidentVideo: boolean
  clipLabel: string
  shouldDefaultToLive: boolean
  hasTimedOut: boolean
  hasClickedLive: boolean
  handleClipClick: () => void
  handleLiveClick: () => void
}

/**
 * Manages the UI state for the clip loader feature.
 *
 * Determines whether to show the collapsed ClipLoader or expanded IncidentVideo,
 * handles user interactions (clicking Live/Clip buttons), and provides the
 * appropriate label for the clip button based on current state.
 */
export const useClipLoaderState = ({
  incidentId,
  incidentType,
  hasVideoClip,
  createdAt,
  isLoading,
}: UseClipLoaderStateIProps): UseClipLoaderStateResultI => {
  const [hasClickedClipAvailable, setHasClickedClipAvailable] = useState(false)
  const [hasClickedLive, setHasClickedLive] = useState(false)
  // Use state (not ref) so initialization triggers re-render
  const [hasClipOnMount, setHasClipOnMount] = useState<boolean | undefined>(
    undefined
  )

  // Reset all state when incident changes
  useEffect(() => {
    setHasClickedClipAvailable(false)
    setHasClickedLive(false)
    setHasClipOnMount(undefined)
  }, [incidentId])

  // Initialize hadClipOnMount in effect to defer until after first render
  // This gives fresh data time to arrive before we lock in the value
  useEffect(() => {
    if (hasClipOnMount === undefined && !isLoading && incidentId) {
      setHasClipOnMount(hasVideoClip)
    }
  }, [hasClipOnMount, isLoading, incidentId, hasVideoClip])

  const isExpectingVideo = !incidentTypesWithoutVideo.includes(incidentType)

  const { hasTimedOut } = useClipLoaderTimeout({
    createdAt,
    hasVideoClip,
    incidentType,
  })

  // Clip is unavailable if timeout expired without clip arriving
  const isClipUnavailable = !hasVideoClip && hasTimedOut

  // Show ClipLoader if started without clip, user hasn't clicked buttons, and clip hasn't arrived
  // Use explicit === false check to ensure data is initialized (not just undefined)
  const shouldShowClipLoader =
    isExpectingVideo &&
    hasClipOnMount === false &&
    !isLoading &&
    !hasTimedOut &&
    !hasClickedClipAvailable &&
    !hasClickedLive &&
    !hasVideoClip

  // Show IncidentVideo if had clip on mount, user clicked a button, or clip arrived after mount
  const shouldShowIncidentVideo =
    hasClipOnMount ||
    hasClickedClipAvailable ||
    hasClickedLive ||
    (hasClipOnMount === false && hasVideoClip)

  const getClipLabel = () => {
    if (isClipUnavailable) {
      return 'Clip unavailable'
    }
    // Only notify when watching live — ClipLoader auto-transitions open the player directly on the clip.
    const didClipArriveAfterMount =
      !hasClipOnMount &&
      hasVideoClip &&
      hasClickedLive &&
      !hasClickedClipAvailable
    if (didClipArriveAfterMount) {
      return 'Clip available'
    }
    return 'Clip'
  }

  const handleClipClick = () => {
    setHasClickedClipAvailable(true)
  }

  const handleLiveClick = () => {
    setHasClickedLive(true)
  }

  const clipLabel = getClipLabel()
  // Start with live if user clicked live and there's no clip yet
  const shouldDefaultToLive = hasClickedLive && !hasVideoClip

  return {
    shouldShowClipLoader,
    shouldShowIncidentVideo,
    clipLabel,
    shouldDefaultToLive,
    hasTimedOut,
    hasClickedLive,
    handleClipClick,
    handleLiveClick,
  }
}
